/**
 * Writing an address value onto a form field, including
 * the two that are `<select>`s.
 *
 * A `<select>` silently refuses a value none of its
 * options carry. The assignment lands, `selectedIndex`
 * goes to -1, and `.value` reads back as the empty
 * string. Nothing throws and nothing in the markup says
 * the write was dropped, so the page goes on believing
 * it holds an address it no longer has.
 *
 * That is fine against a complete list and wrong against
 * both of the address ones, which are neither:
 *
 * - The city list is fetched per state. Between a pick
 *   in a NEW state and the round trip that loads its
 *   cities, the options still belong to the previous
 *   state and every city in the new one is unknown.
 * - `CS.cities` is a convenience, not a register. For
 *   CMX it lists the alcaldías plus "Mexico City" and
 *   carries no "Ciudad de México", which is exactly
 *   what Google answers `locality` with for a CDMX
 *   address. That city is unknown for good.
 *
 * Either way the customer supplied an address and the
 * page threw part of it away. Downstream that reads as
 * NO address: `courier-quote` will not price a trip
 * without a city, so the checkout settles on "Add your
 * address to continue" over a visibly filled-in address
 * and never asks for a quote again (CAL-464).
 *
 * So an unknown value is ADDED to the list rather than
 * dropped from the field. The list exists to save the
 * customer typing, not to rule on what a city may be
 * called: the column behind it is free text and the
 * server geocodes the whole address regardless.
 *
 * The added option is marked, and any previous one is
 * removed before the next write, so a run of picks
 * cannot leave a trail of dead cities behind in the
 * dropdown.
 */
const FALLBACK_FLAG = "fieldValueFallback";

export function setFieldValue(field, value) {
  if (!field) return false;

  const wanted = value == null ? "" : String(value);

  if (field.tagName !== "SELECT") {
    field.value = wanted;
    return false;
  }

  clearFallbackOptions(field);

  field.value = wanted;
  if (field.value === wanted) return false;

  const option = document.createElement("option");
  option.value = wanted;
  option.textContent = wanted;
  option.dataset[FALLBACK_FLAG] = "true";
  field.appendChild(option);
  field.value = wanted;

  return true;
}

/**
 * One place name spelled the way the address lists
 * compare it: NFD-decomposed, combining marks stripped,
 * lowercased. "Cuauhtémoc" and "Cuauhtemoc" are one
 * alcaldía, and `CS.cities` spells it the second way
 * while Google and saved addresses spell it the first.
 * `city-selector` resolves a city onto its list with
 * this, so anything asking whether a select holds a
 * city has to ask the same way.
 */
export function foldAccents(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function clearFallbackOptions(select) {
  Array.from(select.options).forEach((option) => {
    if (option.dataset[FALLBACK_FLAG]) option.remove();
  });
}
